import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FaFile,
  FaFilePdf,
  FaTrash,
  FaEye,
  FaSpinner,
  FaUpload,
} from "react-icons/fa";

const FileUploadField = ({
  field,
  attendeeId,
  value,
  isUploading,
  error,
  onFileChange,
  onFileRemove,
  onUploadStart,
  onUploadEnd,
}) => {
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const acceptedLabel = useMemo(() => {
    if (!field.accept) return null;
    const exts = field.accept
      .split(",")
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    return exts.length ? `Accepted: ${exts.join(", ")}` : null;
  }, [field.accept]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onUploadStart();
    setPreview(URL.createObjectURL(file));

    try {
      const formData = new FormData();
      formData.append("file", file);
      await onFileChange(formData);
    } catch (err) {
      console.error(err);
    } finally {
      onUploadEnd();
      setPreview(null);
    }
  };

  const displayName = (() => {
    if (preview) {
      const file = fileInputRef.current?.files?.[0];
      return file?.name ?? "Uploading…";
    }
    if (value) {
      const raw = decodeURIComponent(value.split("/").pop() ?? "");
      return raw.length > 30 ? raw.slice(0, 30) + "…" : raw;
    }
    return field.placeholder || "Choose file…";
  })();

  const isImage = value && /\.(jpe?g|png|gif|webp)$/i.test(value);
  const isPdf   = value && /\.pdf$/i.test(value);

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={fileInputRef}
        type="file"
        id={`${attendeeId}-${field.id}`}
        style={{ display: "none" }}
        onChange={handleFileSelect}
        disabled={isUploading}
        accept={field.accept ? `.${field.accept.split(",").join(",.")}` : undefined}
        multiple={field.multiple || false}
      />

      <div
        style={{
          border: error ? "2px solid #ef4444" : "2px solid #e5e7eb",
          borderRadius: "8px",
          padding: "12px 16px",
          backgroundColor: error ? "#fef2f2" : "#fafafa",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          cursor: "pointer",
          transition: "border 0.2s, background-color 0.2s",
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        {preview && (
          <div
            style={{
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaSpinner
              style={{ animation: "spin 1s linear infinite" }}
              size={28}
              color="#3b82f6"
            />
          </div>
        )}

        {value && !preview && (
          <>
            {isImage ? (
              <img
                src={value}
                alt={field.label}
                style={{
                  width: 48,
                  height: 48,
                  objectFit: "cover",
                  borderRadius: 4,
                }}
              />
            ) : isPdf ? (
              <FaFilePdf size={32} color="#dc2626" />
            ) : (
              <FaFile size={32} color="#64748b" />
            )}
          </>
        )}

        {!value && !preview && (
          <FaUpload size={28} color="#64748b" />
        )}

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: "14px",
              color: value || preview ? "#1e293b" : "#64748b",
            }}
          >
            {displayName}
          </div>

          {isUploading && !preview && (
            <div style={{ fontSize: "12px", color: "#3b82f6" }}>
              Uploading…
            </div>
          )}
        </div>

        {value && !preview && (
          <div style={{ display: "flex", gap: "4px" }}>
            <button
              type="button"
              title="View file"
              onClick={(e) => {
                e.stopPropagation();
                window.open(value, "_blank");
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#3b82f6",
              }}
            >
              <FaEye size={18} />
            </button>

            <button
              type="button"
              title="Remove file"
              onClick={(e) => {
                e.stopPropagation();
                onFileRemove();
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#ef4444",
              }}
            >
              <FaTrash size={18} />
            </button>
          </div>
        )}
      </div>

      {/* {error && !isUploading && (
        <span
          style={{
            fontSize: "12px",
            color: "#ef4444",
            marginTop: "4px",
            display: "block",
            fontWeight: 500,
          }}
        >
          {error}
        </span>
      )} */}

      {acceptedLabel && !isUploading && (
        <span
          style={{
            fontSize: "12px",
            color: "#64748b",
            marginTop: "4px",
            display: "block",
          }}
        >
          {acceptedLabel}
        </span>
      )}
    </div>
  );
};

const style = document.createElement("style");
style.innerHTML = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default FileUploadField;